import { IEventsQueryCustomization } from '@features/club/hooks/useEventsWithPaging';
import { IEventFilter, IEventSection, IEventTab } from '@features/club/typings-meta';
import { components } from '@features/shared/api/club/clients';

type IEvent = components['schemas']['TournamentShortDto'];

export type ITicketedSection = IEventSection & { ticket: string };

// Тикет, который есть у каждого пользователя
export const ANY_USER_TICKET = '*';
// Маркер «событие без тикетов» в фильтрах, как у query-параметра tickets
export const NO_TICKETS_MARK = '-';

export const DEFAULT_SECTION_TABS: IEventTab[] = [
  { id: 'upcoming', label: 'Ближайшие' },
  { id: 'finished', label: 'Прошедшие' },
  { id: 'drafts', label: 'Черновики' },
];

export function getSectionTabs(section: IEventSection): IEventTab[] {
  return section.tabs ?? DEFAULT_SECTION_TABS;
}

export function isTicketedSection(section: IEventSection): section is ITicketedSection {
  return Boolean(section.ticket);
}

export function buildSectionFilters(section: IEventSection) {
  const scope = getSectionScope(section);
  const tabs = getSectionTabs(section);

  const customFilter = (events: IEvent[], tabId?: string, subId?: string): IEvent[] => {
    if (tabId === 'finished') return events;

    const tab = tabs.find((t) => t.id === tabId);
    const sub = tab?.subTabs?.find((s) => s.id === subId);
    const isDrafts = tabId === 'drafts';

    return events.filter(
      (e) =>
        (e.status === 'Draft') === isDrafts &&
        matchesEventFilter(e, scope) &&
        (!tab?.filter || matchesEventFilter(e, tab.filter)) &&
        (!sub?.filter || matchesEventFilter(e, sub.filter)),
    );
  };

  const customQuery = (tabId?: string): IEventsQueryCustomization | undefined =>
    tabId === 'finished' && scope.tickets ? { tickets: scope.tickets } : undefined;

  return { customFilter, customQuery };
}

// Тикеты турнира для раздела: маркер, входные тикеты и тикет подраздела
export function buildSectionTickets(section: ITicketedSection, subTicket?: string): string[][] {
  return [
    [section.ticket, ...(section.entrance ?? [ANY_USER_TICKET]), ...(subTicket ? [subTicket] : [])],
  ];
}

// Подразделы, которые может назначить форма настроек: ровно один тикет в фильтре
export function getSectionSubTickets(section: IEventSection): { value: string; label: string }[] {
  return getSectionTabs(section).flatMap((tab) =>
    (tab.subTabs ?? []).flatMap((sub) => {
      const tickets = sub.filter?.tickets;
      return tickets?.length === 1 && tickets[0] !== NO_TICKETS_MARK
        ? [{ value: tickets[0]!, label: sub.label }]
        : [];
    }),
  );
}

function getSectionScope(section: IEventSection): IEventFilter {
  if (section.filter) return section.filter;
  return section.ticket ? { tickets: [section.ticket] } : {};
}

function matchesEventFilter(e: IEvent, filter: IEventFilter): boolean {
  if (filter.tickets && !filter.tickets.some((t) => hasTicket(e, t))) return false;
  if (filter.seance !== undefined && (e.system === 'Seance') !== filter.seance) return false;
  if (filter.clockControl && e.clockControl !== filter.clockControl) return false;
  return true;
}

function hasTicket(e: IEvent, ticket: string): boolean {
  const tickets = e.entranceTicketSets?.flat() ?? [];
  return ticket === NO_TICKETS_MARK ? tickets.length === 0 : tickets.includes(ticket);
}
